import {
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import "../config.styles.css";
import "../../../assets/styles/styles.css";

import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import Editor from "@monaco-editor/react";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../utils/permissions";

export const Localization = () => {
    // hooks
    const { enqueueSnackbar } = useSnackbar();
    const editorRef = useRef<any>(null);
    // state
    const [platformMaster, setPlatformMaster] = useState([]);
    const [languageMaster, setLanguageMaster] = useState([]);
    const [selectedPlatform, setSelectedPlatform] = useState<any>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<any>("ENGLISH");
    const [jsonConfig, setJsonConfig] = useState<any>(null);
    const [validJson, setValidJson] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState(true);
    const [loader, setLoader] = useState(false);
    const { hasPermission } = usePermissions();
    const [configId, setConfigId] = useState<any>(null);
    const [localeVersions, setLocaleVersions] = useState<any>([]);
    const [localeVersionLoader, setLocaleVersionLoader] = useState(false);

    useEffect(() => {
        getMasterInformation();
    }, []);

    // when platform is changed in dropdown
    const handleSelectPlatform = (event: any) => {
        setSelectedPlatform(event.target.value);
        getLocaleConfig(selectedLanguage, event.target.value);
    };
    // when language is changed in dropdown
    const handleSelectLanguage = (event: any) => {
        setSelectedLanguage(event.target.value);
        getLocaleConfig(event.target.value, selectedPlatform);
    };

    // make API call for getting the config of the locale
    const getLocaleConfig = async (
        language: any = selectedLanguage,
        platform: any = selectedPlatform
    ) => {
        try {
            // call api only when both inputs are provided
            if (language && platform) {
                setLoader(true);
                const jsonConfigResponse = await axios.get(
                    `${process.env.BASE_URL}api/v1/cms/locale/config?language=${language}&platform=${platform}&fullResponse=true`
                );
                if (jsonConfigResponse.data) {
                    setJsonConfig(jsonConfigResponse.data);
                    setConfigId(jsonConfigResponse.data?.configId);
                    jsonEditorBind(jsonConfigResponse.data?.configJson);
                    fetchLocaleVersions(platform, language);
                }
            }
        } catch (error) {
            console.log("error in getting the local config", error);
            enqueueSnackbar("Error fetching config information", {
                variant: "error",
            });
        } finally {
            setLoader(false);
        }
    };

    // call API for getting the master information
    const getMasterInformation = async () => {
        try {
            const materInformation = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/locale/master`
            );
            if (materInformation.data?.success) {
                setPlatformMaster(materInformation.data?.data?.platform);
                setLanguageMaster(materInformation.data?.data?.language);
            }
        } catch (error) {
            console.log("Error in getting master information", error);
        }
    };

    // whenever any changes happen in JSON editor
    const onJsonConfigChange = (values: any) => {
        validateJson(values);
    };

    // validate the json typed
    const validateJson = (value: any) => {
        try {
            JSON.stringify(JSON.parse(value));
            setValidJson(true);
        } catch (error) {
            setValidJson(false);
        }
        setJsonConfig(value);
        return validJson;
    };

    // when the editor loads for the first, bind it to ref so that it can be used for later manipulation
    function handleEditorDidMount(editor: any, monaco: any) {
        editorRef.current = editor;
    }

    // check if json is valid & bind it in json editor
    const jsonEditorBind = (value: any) => {
        if (value) {
            editorRef.current.setValue(JSON.stringify(sortObjects(value)));
            editorRef.current.getAction("editor.action.formatDocument").run();
        } else {
            enqueueSnackbar("No configuration found", { variant: "warning" });
        }
    };

    // when user clicks on the update button to make changes to the existing configuration
    const onClickEditButton = async () => {
        if (!validateJson(jsonConfig)) {
            enqueueSnackbar("Invalid JSON config, please check", {
                variant: "warning",
            });
        } else if (selectedLanguage && selectedPlatform) {
            editConfiguration();
        } else {
            enqueueSnackbar("Invalid input selected", { variant: "warning" });
        }
    };

    // When user clicks on the edit config button, Make API call for updating in DB
    const editConfiguration = async () => {
        try {
            console.log("jsonConfig", jsonConfig);
            const params = sortObjects(JSON.parse(jsonConfig));
            // Save in DB
            setSubmitting(false);
            setLoader(true);
            const editConfigurationInDB = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/locale?language=${selectedLanguage}&platform=${selectedPlatform}&configId=${configId}`,
                { ...params }
            );
            if (editConfigurationInDB.data?.success) {
                enqueueSnackbar("Localization config updated successfully!", {
                    variant: "success",
                });
                fetchLocaleVersions(selectedPlatform, selectedLanguage);
            } else {
                enqueueSnackbar(editConfigurationInDB.data?.message, {
                    variant: "error",
                });
            }
        } catch (error: any) {
            console.log(
                "Error saving configuration",
                error.response.data.message
            );
            if (error?.response?.data?.message) {
                enqueueSnackbar(error?.response?.data?.message, {
                    variant: "error",
                });
            } else {
                enqueueSnackbar("Error when udpating the localization", {
                    variant: "error",
                });
            }
        } finally {
            setSubmitting(true);
            setLoader(false);
        }
    };

    // sort the objects
    const sortObjects = (jsonObject: any) => {
        try {
            const sortedKeys = Object.keys(jsonObject).sort();
            const sortedData: any = {};
            for (const key of sortedKeys) {
                sortedData[key] = jsonObject[key];
            }
            return sortedData;
        } catch (error) {
            console.log("Unable to sort the objects", error);
            return jsonObject;
        }
    };

    const fetchLocaleVersions = async (platform: string, language: string) => {
        try {
            setLocaleVersionLoader(true);
            const response = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/locale/versions`,
                {
                    params: { platform, language },
                }
            );

            if (response.data) {
                setLocaleVersions(response.data);
            }
        } catch (error) {
            console.log("Error fetching configuration versions", error);
            enqueueSnackbar("Error fetching configuration versions", {
                variant: "error",
            });
            return [];
        } finally {
            setLocaleVersionLoader(false);
        }
    };

    return (
        <div className="max-width">
            <div className="d-flex j-space mb-1">
                {/* Header UI component starts here */}
                <div className="config-header-locale">
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                        <InputLabel id="demo-simple-select-label">
                            Platform
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            className="filter-platform-select"
                            id="demo-simple-select"
                            label="templateSelection"
                            onChange={handleSelectPlatform}
                        >
                            {platformMaster.map((template, index): any => {
                                return (
                                    <MenuItem key={template} value={template}>
                                        {template}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                        <InputLabel id="demo-simple-select-label">
                            Language
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            className="filter-platform-select"
                            id="demo-simple-select"
                            value={selectedLanguage}
                            label="templateSelection"
                            onChange={handleSelectLanguage}
                        >
                            {languageMaster.map((template, index): any => {
                                return (
                                    <MenuItem key={template} value={template}>
                                        {template}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>

                    {loader && <CircularProgress size={30} />}
                </div>
                {/* Header UI component ends here */}

                {/* show button only when the selected platform & language is selected */}
                {hasPermission(PERMISSIONS.locale, ACTION.EDIT) &&
                    selectedLanguage &&
                    selectedPlatform && (
                        <Button
                            disabled={!submitting}
                            onClick={onClickEditButton}
                            className="float-right mt-1"
                            variant="contained"
                        >
                            Save Copy
                        </Button>
                    )}
            </div>
            {/* JSON Editor starts here */}
            <Editor
                theme="vs-dark"
                height="80vh"
                options={{
                    autoIndent: "brackets",
                    wrappingIndent: "indent",
                    wordWrap: "on",
                }}
                className={
                    selectedLanguage && selectedPlatform ? "" : "hide-editor"
                }
                defaultLanguage="json"
                onChange={onJsonConfigChange}
                onMount={handleEditorDidMount}
            />

            {/* JSON Editor ends here */}

            {/* Version Table starts here */}
            {selectedLanguage && selectedPlatform && (
                <>
                    <Typography className="mt-3" variant="body1">
                        Versions List
                    </Typography>
                    {localeVersionLoader ? (
                        <Typography variant="body2">Loading...</Typography>
                    ) : localeVersions?.length === 0 ? (
                        <Typography variant="body2">
                            No versions available.
                        </Typography>
                    ) : (
                        <TableContainer
                            className="mt-2"
                            component={Paper}
                            sx={{ maxWidth: 700 }}
                        >
                            <Table className="version-table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Version Number</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Date of Edit</TableCell>
                                        <TableCell>Version Link</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {localeVersions?.map((version: any) => {
                                        return (
                                            <TableRow key={version.configId}>
                                                <TableCell>
                                                    v
                                                    {version.versionNo.toFixed(
                                                        1
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    {version.modifiedBy || "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {new Date(
                                                        version.lastModified
                                                    ).toLocaleString("en-US", {
                                                        month: "2-digit",
                                                        day: "2-digit",
                                                        year: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                        hour12: true,
                                                    })}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        size="small"
                                                        className="version-link-btn"
                                                        onClick={() =>
                                                            window.open(
                                                                `/json-viewer?configId=${version.configId}&module=locale`,
                                                                "_blank"
                                                            )
                                                        }
                                                        variant="text"
                                                    >
                                                        View Version
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            {/* Version Table ends here */}
        </div>
    );
};
